import React, { useEffect, useMemo, useState } from 'react';
import { fetchCostCategories, fetchNewCostQuantityTypes, addNewProduct } from '../services/api';
import { v4 as uuidv4 } from 'uuid';
import './PageLayout.css';
import { useLocation } from 'react-router-dom';

function NewProductPage() {
  const [categories, setCategories] = useState([]);
  const [quantityTypes, setQuantityTypes] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();

  // read prefill from router or localStorage fallback
  const prefillFromState = location.state?.prefill;
  const prefillFromStorage = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('newProductPrefill') || 'null');
    } catch {
      return null;
    }
  }, []);

  const prefill = prefillFromState || prefillFromStorage || null;

  const [form, setForm] = useState({
    Id: uuidv4(),
    Name: '',
    Category: '',       // CategoryId
    QuantityType: '',   // QuantityTypeId
    // Environmental fields
    Co2Value: '',
    BiogenicValue: '',
    WeightValue: '',
    // New external reference fields
    externalRefType: '',
    ExternalRefId: '',
  });

  // load option lists
  useEffect(() => {
  const fetchData = async () => {
    try {
      console.log('🔄 Fetching categories...');
      const categoriesData = await fetchCostCategories();
      console.log('📦 Categories received:', categoriesData);

      const rawCategories = Array.isArray(categoriesData)
        ? categoriesData
        : Array.isArray(categoriesData?.data)
          ? categoriesData.data
          : [];

      const filteredCategories = rawCategories.filter(c => {
        const name = String(c.Categories || c.Name || '').trim().toLowerCase();
        return name.startsWith('epd');
      });

      console.log('✅ Filtered EPD categories:', filteredCategories);

      setCategories(filteredCategories);
      } catch (error) {
        console.error('❌ Error fetching categories:', error);
        setCategories([]);
      }

    try {
      console.log('🔄 Fetching quantity types...');
      const quantityTypesData = await fetchNewCostQuantityTypes();
      console.log('📦 Quantity types received:', quantityTypesData);
      
      // Ensure we always set an array
      if (Array.isArray(quantityTypesData)) {
        setQuantityTypes(quantityTypesData);
      } else if (quantityTypesData && Array.isArray(quantityTypesData.data)) {
        // Handle case where API returns { data: [...] }
        setQuantityTypes(quantityTypesData.data);
      } else {
        console.warn('⚠️ Quantity types data is not an array:', quantityTypesData);
        setQuantityTypes([]);
      }
    } catch (error) {
      console.error('❌ Error fetching quantity types:', error);
      setQuantityTypes([]);
    }
  };

  fetchData();
}, []);

  // helper: case-insensitive match by name
  const findCategoryIdByName = (name) => {
    if (!name) return '';
    const n = String(name).toLowerCase();
    // your API returns either .Categories or .Name — handle both
    return (
      categories.find(
        (c) => (c.Categories || c.Name || '').toLowerCase() === n
      )?.Id || ''
    );
  };

  const findQuantityTypeIdByName = (name) => {
    if (!name) return '';
    const n = String(name).toLowerCase();
    return quantityTypes.find((q) => (q.Name || '').toLowerCase() === n)?.Id || '';
  };

  // hydrate the form from prefill once options are available
  useEffect(() => {
    if (!prefill) return;
    // Resolve IDs (prefer explicit ids if provided)
    const resolvedCategoryId =
      prefill.CategoryId || findCategoryIdByName(prefill.CategoryName) || '';
    const resolvedQuantityTypeId =
      prefill.QuantityTypeId || findQuantityTypeIdByName(prefill.QuantityTypeName) || '';

    // Safe numeric passthrough (keep empty string if truly empty)
    const numOrEmpty = (v) => (v === '' || v === null || v === undefined ? '' : Number(v));

    setForm((prev) => ({
      ...prev,
      Name: prefill.Name ?? prev.Name,
      Category: String(resolvedCategoryId || prev.Category),
      QuantityType: String(resolvedQuantityTypeId || prev.QuantityType),
      Co2Value: numOrEmpty(prefill.Co2Value),
      BiogenicValue: numOrEmpty(prefill.BiogenicValue),
      WeightValue: numOrEmpty(prefill.WeightValue),
      // New external reference fields from prefill
      externalRefType: prefill.externalRefType ?? prev.externalRefType,
      ExternalRefId: prefill.ExternalRefId ?? prev.ExternalRefId,
    }));
  }, [prefill, categories, quantityTypes]);

  const clearDraft = () => {
    localStorage.removeItem('newProductPrefill');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const generateGUID = () => {
    setForm((f) => ({ ...f, Id: uuidv4() }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formattedData = {
      name: form.Name,
      categoryId: form.Category,
      quantityTypeId: String(form.QuantityType),
      // Environmental data
      co2Value: parseFloat(form.Co2Value) || 0,
      biogenicValue: parseFloat(form.BiogenicValue) || 0,
      weightValue: parseFloat(form.WeightValue) || 0,
      // New external reference fields
      externalRefType: form.externalRefType || null,
      externalRefId: form.ExternalRefId || null,
    };

    console.log('🔹 Submitting Product data:', formattedData);

    try {
      await addNewProduct(formattedData);
      setShowPopup(true);
      clearDraft(); // ✅ remove prefill after success

      // Reset form
      setForm({
        Id: uuidv4(),
        Name: '',
        Category: '',
        QuantityType: '',
        Co2Value: '',
        BiogenicValue: '',
        WeightValue: '',
        externalRefType: '',
        ExternalRefId: '',
      });
    } catch (error) {
      console.error('❌ Error saving product data:', error?.response?.data || error);
      alert('Error saving product data: ' + (error?.response?.data?.error || error.message));
    }
  };

  return (
    <div className="rate-builder-page">
      <h1>New Product Data</h1>
      <p>Add comprehensive product data including cost, CO2, biogenic, and weight values</p>

      {showPopup && (
        <div className="pop-up">
          <div className="pop-up-content">
            <h2>✅ Success</h2>
            <p>New Product Data Added Successfully!</p>
            <button onClick={() => setShowPopup(false)}>OK</button>
          </div>
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div className="form-section">
            <h3>📋 Basic Information</h3>

            <div className="input-group">
              <label>ID</label>
              <div className="input-with-button">
                <input
                  type="text"
                  name="Id"
                  value={form.Id}
                  onChange={(e) => setForm({ ...form, Id: e.target.value })}
                  placeholder="Enter manually or generate"
                />
                <button type="button" onClick={generateGUID}>
                  Generate New ID
                </button>
              </div>
            </div>

            <div className="input-group">
              <label>Product Name *</label>
              <input
                type="text"
                name="Name"
                value={form.Name}
                onChange={handleInputChange}
                placeholder="Enter product name"
                required
              />
            </div>

            <div className="input-group">
              <label>Category *</label>
              <select name="Category" value={form.Category} onChange={handleInputChange} required>
                <option value="">Select Category</option>
                {Array.isArray(categories) && categories.length > 0 ? (
                  categories.map((cat) => (
                    <option key={cat.Id} value={cat.Id}>
                      {cat.Categories}
                    </option>
                  ))
                ) : (
                  <option disabled>Loading categories...</option>
                )}
              </select>
            </div>

            <div className="input-group">
              <label>Quantity Type *</label>
              <select name="QuantityType" value={form.QuantityType} onChange={handleInputChange} required>
                <option value="">Select Quantity Type</option>
                {Array.isArray(quantityTypes) && quantityTypes.length > 0 ? (
                  quantityTypes.map((qt) => (
                    <option key={qt.Id} value={qt.Id}>
                      {qt.Name}
                    </option>
                  ))
                ) : (
                  <option disabled>Loading quantity types...</option>
                )}
              </select>
            </div>

            {/* New External Reference Fields */}
            <div className="input-row">
              <div className="input-group half-width">
                <label>External Reference</label>
                <input
                  type="text"
                  name="externalRefType"
                  value={form.externalRefType}
                  onChange={handleInputChange}
                  placeholder="External reference name or code"
                />
              </div>

              <div className="input-group half-width">
                <label>External Reference ID</label>
                <input
                  type="text"
                  name="ExternalRefId"
                  value={form.ExternalRefId}
                  onChange={handleInputChange}
                  placeholder="External reference identifier"
                />
              </div>
            </div>
          </div>

          {/* Environmental Impact Section */}
          <div className="form-section">
            <h3>🌍 Environmental Impact</h3>

            <div className="input-row">
              <div className="input-group third-width">
                <label>CO2 Value</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    name="Co2Value"
                    value={form.Co2Value}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  <span className="unit-label">kg CO2e</span>
                </div>
              </div>

              <div className="input-group third-width">
                <label>Biogenic Value</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    name="BiogenicValue"
                    value={form.BiogenicValue}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                  <span className="unit-label">kg CO2e</span>
                </div>
              </div>

              <div className="input-group third-width">
                <label>Weight Value</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    name="WeightValue"
                    value={form.WeightValue}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  <span className="unit-label">kg</span>
                </div>
              </div>
            </div>

            <div className="form-note">
              <p>
                💡 <strong>Note:</strong> Environmental impact values are optional but recommended for
                sustainability tracking.
              </p>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-btn">
              ✅ Add Product Data
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewProductPage;
